/* eslint-disable @typescript-eslint/no-explicit-any */
/* ────────────────────────────────────────────────────────────────────────
   Shared, accessible controls for the Salary Structure form sections.

   These enforce the mandatory progressive-disclosure rule: a benefit/deduction
   shows ONLY an "Applicable / Not Applicable" toggle until enabled, then
   reveals its nested fields behind a 2px left rule. Built on the existing
   Input / react-select styling so nothing looks foreign.
   ──────────────────────────────────────────────────────────────────────── */
import React from "react";
import { useFormContext, useController } from "react-hook-form";
import Select from "react-select";
import { customSelectStyles } from "util/selectStyles";

/* Card wrapper for a titled form section. */
export const SectionCard = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <section className="rounded-[14px] border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
    <div className="mb-4 border-b border-slate-100 dark:border-gray-700 pb-2">
      <h3 className="text-sm font-bold text-slate-800 dark:text-white">{title}</h3>
      {subtitle && <p className="text-[12px] text-slate-400 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </section>
);

/* Small helper text under a field. */
export const HelperText = ({ children }: { children: React.ReactNode }) =>
  children ? <p className="text-[11px] text-slate-400 mt-0.5">{children}</p> : null;

/* ⓘ info note explaining a calculation basis. */
export const InfoNote = ({ children }: { children: React.ReactNode }) => (
  <p className="flex items-start gap-1 text-[11px] text-slate-400 mt-1">
    <span className="mt-[1px] inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-slate-300 text-[9px] leading-none">
      i
    </span>
    <span>{children}</span>
  </p>
);

/* Field label with an optional required marker and a unit / basis sub-line. */
export const FieldLabel = ({
  label,
  basis,
  required,
  htmlFor,
}: {
  label: string;
  basis?: string;
  required?: boolean;
  htmlFor?: string;
}) => (
  <label htmlFor={htmlFor} className="block mb-1">
    <span className="text-sm font-medium text-gray-900 dark:text-white">
      {label} {required && <span className="text-red-500">*</span>}
    </span>
    {basis && <span className="block text-[11px] text-slate-400 leading-tight">{basis}</span>}
  </label>
);

/* ── Applicable / Not Applicable toggle with progressive disclosure ─────── */
export const ApplicableToggle = ({
  name,
  label,
  subtitle,
  disabled = false,
  disabledReason,
  children,
}: {
  name: string;
  label: string;
  subtitle?: string;
  disabled?: boolean;
  disabledReason?: string;
  children?: React.ReactNode;
}) => {
  const { control } = useFormContext();
  const { field } = useController({ name, control, defaultValue: false });
  const on = Boolean(field.value);

  const Segment = ({ value, text }: { value: boolean; text: string }) => {
    const selected = on === value;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        disabled={disabled}
        onClick={() => field.onChange(value)}
        className={`px-3 h-[34px] text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 disabled:cursor-not-allowed disabled:opacity-60 ${
          value ? "" : "border-l border-slate-200 dark:border-gray-600"
        } ${
          selected
            ? value
              ? "bg-blue-700 text-white"
              : "bg-slate-500 text-white"
            : "bg-white dark:bg-gray-700 text-slate-500 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-600"
        }`}
      >
        {text}
      </button>
    );
  };

  return (
    <div className="py-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{label}</p>
          {subtitle && <p className="text-[11px] text-slate-400 leading-snug">{subtitle}</p>}
        </div>
        <div
          role="radiogroup"
          aria-label={label}
          className="inline-flex rounded-lg border border-slate-200 dark:border-gray-600 overflow-hidden"
        >
          <Segment value={true} text="Applicable" />
          <Segment value={false} text="Not Applicable" />
        </div>
      </div>
      {disabled && disabledReason && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">{disabledReason}</p>
      )}
      {on && children && (
        <div className="mt-3 ml-1 pl-4 border-l-2 border-slate-200 dark:border-gray-600 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

/* ── Segmented control for small enum / boolean choices ─────────────────── */
export const SegmentedControl = ({
  name,
  label,
  required,
  options,
}: {
  name: string;
  label?: string;
  required?: boolean;
  options: { value: string | boolean; label: string }[];
}) => {
  const { control } = useFormContext();
  const { field } = useController({ name, control });
  return (
    <div>
      {label && <FieldLabel label={label} required={required} />}
      <div
        role="radiogroup"
        aria-label={label}
        className="inline-flex rounded-lg border border-slate-200 dark:border-gray-600 overflow-hidden"
      >
        {options.map((opt, i) => {
          const selected = field.value === opt.value;
          return (
            <button
              key={String(opt.value)}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => field.onChange(opt.value)}
              className={`px-4 h-[38px] text-sm font-medium transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 ${
                i > 0 ? "border-l border-slate-200 dark:border-gray-600" : ""
              } ${
                selected
                  ? "bg-blue-700 text-white"
                  : "bg-white dark:bg-gray-700 text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-600"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ── react-select dropdown bound to react-hook-form ─────────────────────── */
export const SelectField = ({
  name,
  label,
  required,
  options,
  placeholder,
}: {
  name: string;
  label: string;
  required?: boolean;
  options: { value: string; label: string }[];
  placeholder?: string;
}) => {
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });
  const selected = options.find((o) => o.value === field.value) ?? null;
  return (
    <div>
      <FieldLabel label={label} required={required} htmlFor={name} />
      <Select
        inputId={name}
        options={options}
        value={selected}
        placeholder={placeholder || "Select..."}
        styles={customSelectStyles as any}
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        onChange={(opt: any) => field.onChange(opt?.value ?? null)}
      />
      <div className="min-h-[1rem] mt-0.5">
        {error?.message && <p className="text-red-500 text-xs font-medium">{error.message}</p>}
      </div>
    </div>
  );
};

/* Plain checkbox (PF wage ceiling, trainee benefits list). */
export const CheckboxField = ({ name, label }: { name: string; label: string }) => {
  const { control } = useFormContext();
  const { field } = useController({ name, control, defaultValue: false });
  return (
    <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
      <input
        type="checkbox"
        checked={Boolean(field.value)}
        onChange={(e) => field.onChange(e.target.checked)}
        className="w-4 h-4 accent-blue-600"
      />
      {label}
    </label>
  );
};
